import { FC } from 'react';
import { Box, Typography } from '@mui/material';
import { LandingLayout } from '../src/layouts/LandingLayout';
import { Link } from '../src/components/primitives/Link';

const H3: FC = ({ children }) => (
  <Typography
    color="text.primary"
    variant="h3"
    sx={{
      xs: 4,
      lg: 6,
      ['+h3']: {
        mt: 3,
      }
    }}
  >
    {children}
  </Typography>
)

const Paragraph: FC = ({ children }) => (
  <Box
    color="text.secondary"
    sx={{
      textShadow: '#000 0 0 5px, #000 0 0 10px',
    }}
  >
    {children}
  </Box>
)

export default function TermsOfService() {
  return (
    <Box sx={{
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: { xs: 4, lg: 8 },
      px: { xs: 4, lg: 0 },
      pt: { xs: 8, lg: 12 },
      pb: { xs: 12, lg: 24 }
    }}>
      <Typography variant="h1" sx={{ mb: 2 }}>Terms of Service</Typography>
      <Paragraph>
        <H3>YLDR Terms of Service</H3>
        <H3>Introduction</H3>
        <Typography variant="secondary14">
          Welcome to YLDR, a pioneering decentralized finance (DeFi) platform offering a
          lending protocol designed for Uniswap V3 and other decentralized exchanges (DEXes) utilizing concentrated
          liquidity algorithms. These Terms of Service (ToS) govern your access to and use of YLDR&lsquo;s services. By engaging
          with our platform, you agree to these ToS. If you do not agree to these terms, you must not use our
          services.
        </Typography>
      </Paragraph>
      <Paragraph>
        <H3>Agreement to Terms</H3>
        <Typography variant="secondary14">
          By using YLDR, you confirm that you have read, understood, and agree to be bound by these ToS. If you are
          accessing our services on behalf of a legal entity, you represent that you have the authority to agree to these
          terms on behalf of that entity.
        </Typography>
      </Paragraph>
      <Paragraph>
        <H3>Changes to Terms</H3>
        <Typography variant="secondary14">
          YLDR reserves the right, at its discretion, to modify or replace these ToS at any time. If a revision is
          material, we will provide at least 30 days&lsquo; notice prior to any new terms taking effect. What constitutes a
          material change will be determined at our sole discretion.
        </Typography>
      </Paragraph>
      <Paragraph>
        <H3>Eligibility and Access Restrictions</H3>
        <Typography variant="secondary14">
          To engage with the YLDR platform, certain geographical restrictions apply. By accessing or using YLDR, you
          affirm that you do not reside in, nor hold citizenship from, regions subjected to sanctions or embargoes imposed
          by the United States of America. This restriction encompasses but is not limited to the following locations:
        </Typography>
        <ol>
          <li>
            <Typography variant="secondary14">
              The United States of America
            </Typography>
          </li>
          <li>
            <Typography variant="secondary14">
              North Korea
            </Typography>
          </li>
          <li>
            <Typography variant="secondary14">
              Sudan
            </Typography>
          </li>
          <li>
            <Typography variant="secondary14">
              Crimea and Sevastopol
            </Typography>
          </li>
          <li>
            <Typography variant="secondary14">
              Cuba
            </Typography>
          </li>
          <li>
            <Typography variant="secondary14">
              Syria
            </Typography>
          </li>
          <li>
            <Typography variant="secondary14">
              United Kingdom
            </Typography>
          </li>
          <li>
            <Typography variant="secondary14">
              Canada
            </Typography>
          </li>
          <li>
            <Typography variant="secondary14">
              Belgium
            </Typography>
          </li>
        </ol>
        <Typography variant="secondary14">
          Additionally, you assure that your current location does not fall within any territory, country, or jurisdiction
          where local laws or regulations would forbid your participation in our services, or where such participation
          would be otherwise illegal or non-compliant with local legislation. Your compliance with these terms is a
          prerequisite for your use of YLDR&lsquo;s services.
        </Typography>
      </Paragraph>
      <Paragraph>
        <H3>Description of Services</H3>
        <Typography variant="secondary14">
          YLDR offers an access to a blockchain-based lending protocol allowing users to lend or borrow assets, and
          leverage their liquidity provision positions. Our services include but are not limited to, providing liquidity,
          executing transactions, and managing collateral.
        </Typography>
      </Paragraph>
      <Paragraph>
        <H3>Assumption of Risk</H3>
        <Typography variant="secondary14">
          You acknowledge that the cryptocurrency market is highly volatile and that participating in DeFi protocols
          involves significant risk, including but not limited to, the risk of financial loss, regulatory action, and
          technological issues. YLDR is not liable for any decisions you make based on your use of our platform.
        </Typography>
      </Paragraph>
      <Paragraph>
        <H3>Intellectual Property Rights</H3>
        <Typography variant="secondary14">
          All intellectual property rights in the services, technology, and content provided by YLDR are owned by us or
          our licensors. You are granted a limited, non-exclusive, non-transferable, and revocable license to use our
          services according to these ToS.
        </Typography>
      </Paragraph>
      <Paragraph>
        <H3>Prohibited Use</H3>
        <Typography variant="secondary14">
          You agree not to use YLDR in any manner that is illegal or harmful to any person or entity. This includes but is
          not limited to, engaging in fraud, money laundering, or financing terrorism.
        </Typography>
      </Paragraph>
      <Paragraph>
        <H3>Limitation of Liability</H3>
        <Typography variant="secondary14">
          YLDR will not be liable for any indirect, incidental, special, consequential, or punitive damages, including
          without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your access
          to or use of or inability to access or use our services.
        </Typography>
      </Paragraph>
      <Paragraph>
        <H3>Governing Law</H3>
        <Typography variant="secondary14">
          These ToS shall be governed by and construed in accordance with the laws of the United Arab Emirates, without
          regard to its conflict of law provisions.
        </Typography>
      </Paragraph>
      <Paragraph>
        <H3>Dispute Resolution</H3>
        <Typography variant="secondary14">
          All disputes related to these ToS will be resolved by arbitration in accordance with the arbitration rules of
          the jurisdiction where YLDR is established.
        </Typography>
      </Paragraph>
      <Paragraph>
        <H3>Contact Information</H3>
        <Typography variant="secondary14">
          If you have any questions about these ToS, please contact us at <Link href="mailto:admin@yldr.com">admin@yldr.com</Link>.
        </Typography>
      </Paragraph>
    </Box>
  );
}

TermsOfService.getLayout = function getLayout(page: React.ReactElement) {
  return <LandingLayout>{page}</LandingLayout>;
};
